import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { mockData } from '../data/mock';
import { api } from '../services/api';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await api.submitContactForm(formData);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      }, 3000);
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: mockData.company.address,
      gradient: 'from-[#003366] to-[#339933]'
    },
    {
      icon: Phone,
      title: 'Phone',
      content: mockData.company.phone,
      gradient: 'from-[#339933] to-[#003366]'
    },
    {
      icon: Mail,
      title: 'Email',
      content: `${mockData.company.email}\n${mockData.company.adminEmail}`,
      gradient: 'from-[#003366] to-[#339933]'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-[#00B050]/5 via-white to-[#1E4E9E]/5">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-4">
            Get In <span className="bg-gradient-to-r from-[#003366] via-[#339933] to-[#003366] bg-clip-text text-transparent">Touch</span>
          </h2>
          <p className="text-lg text-slate-600">
            Ready to start your project? Contact us today for a consultation
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card
                  key={index}
                  className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-white overflow-hidden group"
                >
                  <div className={`h-1 w-full bg-gradient-to-r ${info.gradient} group-hover:h-2 transition-all duration-300`}></div>
                  <CardContent className="p-6">
                    <div
                      className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${info.gradient} shadow-lg`}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{info.title}</h3>
                    <p className="text-slate-600 text-sm whitespace-pre-line leading-relaxed">
                      {info.content}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-xl bg-white">
              <CardContent className="p-8">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                      </div>
                    )}
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Your Name *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          disabled={isSubmitting}
                          className="border-slate-200 focus:border-[#003366] focus:ring-[#1E4E9E] transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          disabled={isSubmitting}
                          className="border-slate-200 focus:border-[#003366] focus:ring-[#1E4E9E] transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Phone Number
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+670 1234567"
                          disabled={isSubmitting}
                          className="border-slate-200 focus:border-[#003366] focus:ring-[#1E4E9E] transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Subject *
                        </label>
                        <Input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Project Inquiry"
                          required
                          disabled={isSubmitting}
                          className="border-slate-200 focus:border-[#003366] focus:ring-[#1E4E9E] transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project..."
                        required
                        disabled={isSubmitting}
                        rows={6}
                        className="border-slate-200 focus:border-[#003366] focus:ring-[#1E4E9E] transition-all resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[#003366] to-[#339933] hover:from-[#339933] hover:to-[#003366] text-white transition-all duration-500 transform hover:scale-105 shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      {!isSubmitting && <Send className="ml-2 w-5 h-5" />}
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gradient-to-br from-[#339933] to-[#003366] rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                      <CheckCircle className="w-14 h-14 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                    <p className="text-slate-600 text-lg">
                      Thank you for contacting us. We'll get back to you shortly.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};